<template>
	<div
		id="main-navigation"
		class="navigation"
		:class="{ 'navigation--overlay-open': navigation.overlayOpen }">
		<!-- On narrow screens the panel is an overlay: out of reach while closed, a modal while open. -->
		<div
			ref="panel"
			class="navigation__panel"
			:aria-label="overlay ? 'Meni' : undefined"
			:aria-modal="overlay && navigation.overlayOpen ? 'true' : undefined"
			:inert="overlay && !navigation.overlayOpen ? true : undefined"
			:role="overlay && navigation.overlayOpen ? 'dialog' : undefined"
			@keydown.tab="keepFocusInside">
			<div class="navigation__header">
				<span
					aria-hidden="true"
					class="navigation__brand-mark">
					<FontAwesomeIcon icon="fa-solid fa-file-invoice" />
				</span>
				<span class="navigation__brand-name">Računi</span>
			</div>
			<nav
				aria-label="Glavna navigacija"
				class="navigation__links-container">
				<NavigationLink
					v-for="link in links"
					:key="link.title"
					:icon="link.icon"
					:title="link.title"
					:to="link.to" />
			</nav>
			<div
				class="navigation__footer"
				:class="{ 'navigation__footer--active': currentRouteName === 'account-details' }">
				<RouterLink
					class="navigation__account"
					:aria-label="`Moj profil: ${auth.user!.email}`"
					:to="{ name: 'account-details' }"
					@click="navigation.close()">
					<Avatar
						aria-hidden="true"
						class="navigation__avatar"
						shape="circle"
						:label="initials" />
					<span class="navigation__identity">
						<span class="navigation__name">{{ auth.user!.name }}</span>
						<span class="navigation__email">{{ auth.user!.email }}</span>
					</span>
				</RouterLink>
				<Button
					v-tooltip.top="'Odjava'"
					aria-label="Odjava"
					class="navigation__logout-button"
					icon="fat fa-arrow-right-from-bracket"
					severity="secondary"
					text
					@click="logout" />
			</div>
		</div>
		<Transition name="nav-scrim">
			<div
				v-if="navigation.overlayOpen"
				class="navigation__scrim"
				@click="navigation.close()"></div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/stores/Auth'
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import NavigationLink from '@/components/NavigationLink.vue'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'
import { useNavigationStore } from '@/stores/Navigation'

const auth = useAuthStore()
const router = useRouter()
const navigation = useNavigationStore()

// The billing workflow first; settings and user management only for admins, as their routes.
const allLinks: { title: string; icon: string; to: RouteLocationRaw; roles?: string[] }[] = [
	{ title: 'Računi', icon: 'fa-regular fa-file-invoice', to: { name: 'invoices-list' } },
	{ title: 'Stranke', icon: 'fa-regular fa-users', to: { name: 'customers-list' } },
	{
		title: 'Nastavitve',
		icon: 'fa-regular fa-building',
		to: { name: 'business-profile' },
		roles: ['admin'],
	},
	{
		title: 'Uporabniki',
		icon: 'fa-regular fa-user-gear',
		to: { name: 'users-list' },
		roles: ['admin'],
	},
]
const links = computed(() =>
	allLinks.filter((link) => !link.roles || link.roles.includes(auth.user!.role)),
)

// Initials of the name, as "AN" for Ana Novak; the email's first letters for an account without one.
const initials = computed(() => {
	const words = (auth.user!.name ?? '').trim().split(/\s+/).filter(Boolean)
	const letters =
		words.length > 1 ? words[0]![0]! + words[words.length - 1]![0]! : auth.user!.email.slice(0, 2)
	return letters.toUpperCase()
})
const currentRouteName = computed(() => router.currentRoute.value.name)
function logout() {
	navigation.close()
	router.push({ name: 'logout' })
}

// Up to 1024 px wide the navigation is an overlay that the header's menu button opens.
const OVERLAY_QUERY = '(max-width: 1024px)'
const overlay = ref(false)
let media: MediaQueryList | null = null
function followMedia() {
	overlay.value = !!media?.matches
}

// Opening moves the focus to the first link and keeps it in the panel; closing returns it to
// where it was, usually the menu button. A link that opens another page leaves it there.
const panel = ref<HTMLElement | null>(null)
let opener: HTMLElement | null = null
const focusable = () => [
	...(panel.value?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? []),
]

watch(
	() => navigation.overlayOpen,
	async (open) => {
		if (!overlay.value) return
		if (open) {
			opener = document.activeElement as HTMLElement | null
			await nextTick()
			focusable()[0]?.focus()
		} else {
			if (opener?.isConnected) opener.focus()
			opener = null
		}
	},
)

function keepFocusInside(event: KeyboardEvent) {
	if (!overlay.value || !navigation.overlayOpen) return
	const items = focusable()
	if (!items.length) return
	const first = items[0]!
	const last = items[items.length - 1]!
	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault()
		last.focus()
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault()
		first.focus()
	}
}

// Escape closes the narrow-screen menu wherever the focus is.
function closeOnEscape(event: KeyboardEvent) {
	if (event.key === 'Escape' && navigation.overlayOpen) navigation.close()
}
onMounted(() => {
	media = window.matchMedia(OVERLAY_QUERY)
	followMedia()
	media.addEventListener?.('change', followMedia)
	document.addEventListener('keydown', closeOnEscape)
})
onBeforeUnmount(() => {
	media?.removeEventListener?.('change', followMedia)
	document.removeEventListener('keydown', closeOnEscape)
})
</script>

<style scoped lang="scss">
.navigation {
	border-right: 1px solid var(--p-content-border-color);
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	overflow: hidden;
	width: 248px;
	background-color: var(--p-content-background);
	position: relative;
	z-index: 400;

	.navigation__panel {
		display: flex;
		flex-direction: column;
		height: 100%;
		z-index: 4001;
	}

	.navigation__header {
		display: flex;
		align-items: center;
		gap: 10px;
		height: 64px;
		padding: 0 20px;
		flex-shrink: 0;

		.navigation__brand-mark {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			border-radius: 9px;
			background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-700));
			color: #ffffff;
			font-size: 15px;
			box-shadow: 0 1px 2px rgba(4, 120, 87, 0.3);
		}

		.navigation__brand-name {
			font-size: 17px;
			font-weight: 650;
			letter-spacing: -0.01em;
			color: var(--p-text-color);
		}
	}

	.navigation__links-container {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1;
		overflow: auto;
		padding: 8px 12px;
	}

	.navigation__footer {
		display: flex;
		align-items: center;
		gap: 4px;
		margin: 12px;
		padding: 6px;
		border: 1px solid var(--p-content-border-color);
		border-radius: 10px;
		flex-shrink: 0;
		transition: background-color 0.15s ease;

		&:has(.navigation__account:hover),
		&--active {
			background-color: var(--p-surface-100);

			@media (prefers-color-scheme: dark) {
				background-color: var(--p-surface-800);
			}
		}

		.navigation__account {
			display: flex;
			align-items: center;
			gap: 10px;
			flex: 1;
			min-width: 0;
			padding: 2px;
			color: inherit;
			text-decoration: none;
			border-radius: 6px;

			&:focus-visible {
				outline: 2px solid var(--p-primary-500);
				outline-offset: 2px;
			}
		}

		.navigation__avatar {
			flex-shrink: 0;
			width: 32px;
			height: 32px;
			font-size: 12px;
			font-weight: 600;
			background: var(--p-primary-100);
			color: var(--p-primary-800);

			@media (prefers-color-scheme: dark) {
				background: var(--p-primary-900);
				color: var(--p-primary-100);
			}
		}

		.navigation__identity {
			display: flex;
			flex-direction: column;
			min-width: 0;
			line-height: 1.3;
		}

		.navigation__name {
			font-size: 13px;
			font-weight: 600;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.navigation__email {
			font-size: 12px;
			color: var(--p-text-muted-color);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	@media only screen and (max-width: 1024px) {
		position: fixed;
		inset: 0;
		width: 100vw;
		height: 100dvh;
		border-right: none;
		background: transparent;
		pointer-events: none;
		z-index: 4000;

		.navigation__panel {
			position: absolute;
			left: 0;
			top: 0;
			width: 272px;
			height: 100%;
			background: var(--p-content-background);
			border-right: 1px solid var(--p-content-border-color);
			transform: translateX(-100%);
			transition:
				transform 0.25s ease,
				box-shadow 0.25s ease;
			will-change: transform;
			z-index: 4002;
		}

		&.navigation--overlay-open {
			pointer-events: auto;

			.navigation__panel {
				transform: translateX(0);
				box-shadow: 0 10px 30px rgba(15, 23, 42, 0.2);
			}
		}

		.navigation__scrim {
			position: fixed;
			inset: 0;
			background: rgba(15, 23, 42, 0.35);
			z-index: 4001;
			pointer-events: auto;
			opacity: 1;
			backdrop-filter: blur(3px);
		}

		.nav-scrim-enter-from,
		.nav-scrim-leave-to {
			opacity: 0;
			backdrop-filter: blur(0px);
		}
		.nav-scrim-enter-active,
		.nav-scrim-leave-active {
			transition:
				opacity 0.2s ease,
				backdrop-filter 0.25s ease,
				-webkit-backdrop-filter 0.25s ease;
			will-change: opacity, backdrop-filter;
		}
		.nav-scrim-enter-to,
		.nav-scrim-leave-from {
			opacity: 1;
			backdrop-filter: blur(3px);
		}
	}
}
</style>
